import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  MapPin, 
  FileText, 
  AlertTriangle,
  ArrowRight,
  Globe,
  Link as LinkIcon,
  XCircle
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ServiceAreaPagesLocalSEO = () => {
  const faqs = [
    {
      question: "What is a service area page?",
      answer: "A service area page is a dedicated page on your website that targets a specific city or town where you provide services. It helps you rank in local searches for that area, like 'lawn care in Springfield' or 'landscaping near Riverside.'"
    },
    {
      question: "How many service area pages do I need?",
      answer: "Focus on 5–15 cities where you actively want more work. Prioritize cities with higher populations, less competition, or where you already have happy customers. Quality matters more than quantity—10 strong pages beat 50 thin ones."
    },
    {
      question: "Will service area pages hurt my SEO if I create too many?",
      answer: "Yes, if they're thin or duplicated. Google penalizes doorway pages (copy-paste content with only the city name swapped). Each page needs unique, helpful content specific to that area. If you can't write something meaningful about a city, don't create a page for it."
    },
    {
      question: "Do I need a physical address in each city to rank there?",
      answer: "No. Service-area businesses can rank without a physical location in each city. What matters is having a well-structured service area page, being listed on Google Business Profile as serving that area, and earning local signals like reviews mentioning that city."
    },
    {
      question: "How long does it take for service area pages to rank?",
      answer: "Typically 2–6 months for competitive keywords. Smaller cities with less competition may rank faster. Consistent local SEO efforts—like getting reviews that mention the city—speed things up."
    },
    {
      question: "Should I include pricing on service area pages?",
      answer: "You can include general pricing ranges or a 'starting at' price. This helps qualify leads. However, specific quotes should come from your contact form. Pricing transparency builds trust and reduces tire-kickers."
    },
    {
      question: "Can I use service area pages for surrounding counties or regions?",
      answer: "Yes. You can create pages for counties, neighborhoods, or regions (like 'North Shore' or 'Metro Atlanta'). Use the geographic terms your customers actually search for."
    },
    {
      question: "What's the difference between service area pages and location pages?",
      answer: "Location pages are for businesses with physical storefronts in multiple places. Service area pages are for businesses that travel to customers. Most lawn care and landscaping companies need service area pages, not location pages."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/service-area-pages-local-seo/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/service-area-pages-local-seo/#webpage",
    "name": "Service Area Pages for Local SEO | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/service-area-pages-local-seo/",
    "description": "Learn how to create service area pages that help your lawn care or landscaping business rank in nearby cities. Best practices, structure, and common mistakes.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Service Area Pages for Local SEO"
        description="Learn how to create service area pages that help your lawn care or landscaping business rank in nearby cities. Best practices, structure, and common mistakes."
        keywords="service area pages, local SEO service area pages, city pages for landscapers, lawn care service areas, rank in nearby cities, service area page SEO"
        canonical="https://www.leafrushmarketing.com/service-area-pages-local-seo/"
        ogUrl="https://www.leafrushmarketing.com/service-area-pages-local-seo/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Local SEO Guide
              </Badge>
              
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
                Service Area Pages: How to Rank in Nearby Cities
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Your <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink> or <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> needs service area pages to rank in nearby cities.
              </p>
              
              <p className="text-muted-foreground">
                We build service area pages into every lawn care web design and landscaping web design—helping you get found in every city you serve without opening new locations.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    See Website Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* What Are Service Area Pages */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">What Are Service Area Pages?</h2>
              
              <p className="text-muted-foreground mb-4">
                A service area page is a dedicated page on your website targeting a specific city, town, or region where you provide services.
              </p>
              
              <p className="text-muted-foreground mb-4">
                Instead of one generic "Service Areas" page listing 20 cities, you create individual pages like:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>yoursite.com/lawn-care-springfield/</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>yoursite.com/landscaping-riverside/</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>yoursite.com/hardscaping-oak-park/</span>
                </li>
              </ul>
              
              <p className="text-muted-foreground">
                Each page gives Google a clear signal: "This business serves this area." That's how you show up when someone in Riverside searches "landscaper near me"—even if your main office is 20 miles away.
              </p>
              
              <div className="mt-8 p-4 bg-card rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  <strong>Related:</strong> We build service area pages into every website. See our{" "}
                  <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink> and{" "}
                  <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> packages.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* When You Need Service Area Pages */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">When You Need Service Area Pages (and When You Don't)</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      You Need Them If...
                    </h3>
                    <ul className="space-y-3 text-sm">
                      <li>• You serve 3+ cities/towns beyond your main location</li>
                      <li>• You want more jobs from a specific high-value area</li>
                      <li>• Competitors are ranking in cities you also serve</li>
                      <li>• You get calls asking "Do you service [city]?"</li>
                      <li>• You're expanding into new territories</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-500/5">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      Skip Them If...
                    </h3>
                    <ul className="space-y-3 text-sm">
                      <li>• You only serve one small town</li>
                      <li>• You're fully booked and not looking to expand</li>
                      <li>• You can't write unique content for each city</li>
                      <li>• The cities are too small to have search volume</li>
                      <li>• You plan to copy-paste the same content everywhere</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-muted-foreground">
                Bottom line: If you want more calls from specific cities, service area pages are one of the most effective ways to get them.
              </p>
            </div>
          </div>
        </section>

        {/* Best Practice Structure */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Best-Practice Structure for Service Area Pages</h2>
              
              <p className="text-muted-foreground mb-8">
                Each city page should follow this proven structure:
              </p>
              
              <div className="space-y-6">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">1. City-Specific H1 Heading</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Example: "Professional Lawn Care in Springfield, IL" — not just "Lawn Care Services"
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">2. Opening Paragraph With Local Details</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Mention something specific: "Serving homeowners from downtown Springfield to the Sherman Park neighborhood."
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">3. Services Offered in That City</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    List the services you actually provide there. Link to your main service pages.
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">4. Why Hire a Local Provider</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Explain benefits: faster response time, knowledge of local soil/climate, community reputation.
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">5. Photos or Gallery (Optional but Powerful)</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Show work you've done in that city. "Before and after: patio installation on Oak Street, Springfield."
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">6. Testimonial From That Area</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    A review mentioning the city adds credibility and local relevance.
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold text-lg">7. Clear Call-to-Action</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    "Get a free quote for lawn care in Springfield" with phone number and form.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How to List Cities Without Spam */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">How to List Cities Without Looking Spammy</h2>
              
              <p className="text-muted-foreground mb-6">
                You've seen those footers stuffed with 50 city names. That's a red flag to Google. Here's how to list your service areas naturally:
              </p>
              
              <div className="space-y-6">
                <div className="p-4 bg-card rounded-lg border">
                  <h3 className="font-semibold text-red-600 mb-2">❌ Don't Do This:</h3>
                  <p className="text-sm text-muted-foreground italic">
                    "We serve Springfield, Riverside, Oak Park, Greenfield, Lakeview, Hillside, Westbrook, Fairview, Pleasant Valley, Cedar Grove, Maple Heights, Sunset Hills, North Ridge, South Haven, East Point..."
                  </p>
                </div>
                
                <div className="p-4 bg-card rounded-lg border">
                  <h3 className="font-semibold text-green-600 mb-2">✓ Do This Instead:</h3>
                  <p className="text-sm text-muted-foreground">
                    "We provide lawn care and landscaping services throughout the Greater Springfield area, including Riverside, Oak Park, and Greenfield. See our full list of service areas or call to confirm we cover your neighborhood."
                  </p>
                </div>
              </div>
              
              <h3 className="font-semibold text-lg mt-8 mb-4">Natural Wording Examples:</h3>
              
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>"Proudly serving homeowners in Springfield and surrounding communities"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>"Based in Riverside, we travel up to 25 miles for projects"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>"Our most popular service areas include Oak Park, Greenfield, and Lakeview"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>"Not sure if we cover your area? Give us a call—we're happy to check."</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Internal Linking Map */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Internal Linking Map: How Pages Connect</h2>
              
              <p className="text-muted-foreground mb-6">
                Service area pages work best when they're woven into your site structure. Here's how the linking should flow:
              </p>
              
              <div className="p-6 bg-card rounded-lg border mb-6">
                <div className="flex items-center justify-center gap-2 text-sm mb-4">
                  <Globe className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Homepage</span>
                </div>
                
                <div className="flex justify-center mb-4">
                  <ArrowRight className="h-5 w-5 text-muted-foreground rotate-90" />
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div className="p-3 bg-accent/50 rounded">
                    <FileText className="h-4 w-4 mx-auto mb-1 text-primary" />
                    <span>Service Pages</span>
                  </div>
                  <div className="p-3 bg-primary/10 rounded border border-primary/30">
                    <MapPin className="h-4 w-4 mx-auto mb-1 text-primary" />
                    <span className="font-semibold">City Pages</span>
                  </div>
                  <div className="p-3 bg-accent/50 rounded">
                    <FileText className="h-4 w-4 mx-auto mb-1 text-primary" />
                    <span>About/Contact</span>
                  </div>
                </div>
              </div>
              
              <h3 className="font-semibold text-lg mb-4">Linking Rules:</h3>
              
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Homepage → City pages:</strong> Link from a "Service Areas" section or dropdown menu</span>
                </li>
                <li className="flex items-start gap-2">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>City pages → Service pages:</strong> "Learn more about our lawn mowing services"</span>
                </li>
                <li className="flex items-start gap-2">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Service pages → City pages:</strong> "We offer this service in Springfield, Riverside, and Oak Park"</span>
                </li>
                <li className="flex items-start gap-2">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>City pages → Other city pages:</strong> "Also serving nearby Greenfield and Lakeview"</span>
                </li>
              </ul>
              
              <p className="text-sm text-muted-foreground mt-6">
                This creates a web of related content that helps both users and search engines navigate your site.
              </p>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Common Mistakes We Fix</h2>
              
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Duplicate Content Across City Pages</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> Copy-pasting the same content and only swapping the city name.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Write unique introductions, mention local landmarks, and use city-specific photos/reviews.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Thin Content (Under 300 Words)</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> Pages with just a paragraph and a contact form.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Aim for 500–800 words with real substance—services, benefits, FAQs, testimonials.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">No Internal Links</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> City pages are orphaned—no links in or out.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Link from homepage, service pages, and nearby city pages. Add city pages to your main navigation.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Missing or Weak CTAs</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> No clear way to contact you on the page.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Add a sticky phone button, embed a quote form, and include "Get a quote in [City]" CTAs.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Ignoring Google Business Profile</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> City pages exist, but GBP isn't set up to match.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Your{" "}
                          <NavLink to="/growth-add-ons/gbp" className="text-primary hover:underline">Google Business Profile</NavLink>{" "}
                          should list all your service areas. Website and GBP work together.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Mini Checklist */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Service Area Page Checklist</h2>
              
              <p className="text-muted-foreground mb-6">
                Use this quick checklist for every city page you create:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "City name in H1 and title tag",
                  "Unique opening paragraph (not copy-paste)",
                  "500+ words of helpful content",
                  "Services listed with links to service pages",
                  "Local photo or before/after gallery",
                  "Testimonial from that area (if available)",
                  "Contact form or click-to-call button",
                  "Links to/from homepage and service pages",
                  "Meta description with city name",
                  "Mobile-friendly layout and fast load time"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-card rounded-lg border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 bg-card rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  <strong>Free resource:</strong> Download our full{" "}
                  <NavLink to="/resources" className="text-primary hover:underline">lawn care website checklist</NavLink>{" "}
                  for more optimization tips.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Frequently Asked Questions</h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-primary/5">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Want Us to Build This Into Your Site?</h2>
              
              <p className="text-muted-foreground mb-6">
                We build service area pages into every{" "}
                <NavLink to="/websites" className="text-primary hover:underline">website subscription</NavLink>. 
                You tell us the cities—we create the pages, handle the SEO structure, and connect everything properly.
              </p>
              
              <p className="text-muted-foreground mb-8">
                Have questions about your specific situation? Let's talk. Book a 15-minute call or{" "}
                <NavLink to="/contact" className="text-primary hover:underline">send us a message</NavLink>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Related Reading */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl font-bold mb-6">Related Reading</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Done-for-you websites for lawn care companies</p>
                </NavLink>
                
                <NavLink to="/landscaping-website-design/" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Websites for landscapers that convert visitors</p>
                </NavLink>
                
                <NavLink to="/growth-add-ons/gbp" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Google Business Profile Optimization</h3>
                  <p className="text-sm text-muted-foreground">Get more visibility in Google Maps</p>
                </NavLink>
                
                <NavLink to="/blog" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Marketing Blog</h3>
                  <p className="text-sm text-muted-foreground">Tips and strategies for growing your business</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly Section */}
        <CalendlyEmbed />

        {/* Last Updated */}
        <section className="py-8">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm text-muted-foreground">
                Last updated: December 19, 2024
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServiceAreaPagesLocalSEO;
